import { api } from '../api/client';
import { generateKey, open, seal, type Sealed } from './crypto';

/**
 * File de commandes saisies hors ligne au guichet (§18). Contenu chiffré dans IndexedDB ; seules la création et la
 * modification de brouillons sont admises. La synchronisation est idempotente (identifiant de commande) et les
 * conflits sont signalés, jamais écrasés.
 */

export interface OfflineCommand {
  commandId: string;
  type: 'brouillon_guichet.creer' | 'brouillon.modifier';
  applicationId?: string;
  knownVersion?: number;
  payload: Record<string, unknown>;
  createdAt: string;
  label: string;
}

export interface SyncOutcome {
  commandId: string;
  outcome: 'appliquee' | 'conflit' | 'rejetee';
  replay: boolean;
  detail?: string;
  result?: { applicationId?: string; reference?: string; trackingCode?: string; currentVersion?: number };
}

const DB_NAME = 'casijud-hors-ligne';
const DEVICE_KEY = 'casijud.poste';

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore('cles');
      req.result.createObjectStore('commandes', { keyPath: 'commandId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  db: IDBDatabase,
  store: string,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const req = run(db.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function key(db: IDBDatabase): Promise<CryptoKey> {
  const existing = await tx<CryptoKey | undefined>(db, 'cles', 'readonly', (s) => s.get('principale'));
  if (existing) return existing;
  const created = await generateKey();
  await tx(db, 'cles', 'readwrite', (s) => s.put(created, 'principale'));
  return created;
}

/** Identifiant du poste (pratique locale : non secret). */
export function deviceId(): string {
  try {
    let id = window.localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = `poste-${crypto.randomUUID()}`;
      window.localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return `poste-${crypto.randomUUID()}`;
  }
}

export async function enqueue(command: Omit<OfflineCommand, 'commandId' | 'createdAt'>): Promise<void> {
  const db = await idb();
  const full: OfflineCommand = {
    ...command,
    commandId: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
  };
  const sealed = await seal(await key(db), full);
  await tx(db, 'commandes', 'readwrite', (s) => s.put({ commandId: full.commandId, sealed }));
}

export async function pending(): Promise<OfflineCommand[]> {
  const db = await idb();
  const k = await key(db);
  const rows = await tx<{ commandId: string; sealed: Sealed }[]>(db, 'commandes', 'readonly', (s) =>
    s.getAll(),
  );
  const out = await Promise.all(rows.map((r) => open<OfflineCommand>(k, r.sealed)));
  return out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Transmet la file ; retire les commandes appliquées ou rejetées, conserve les conflits pour examen. */
export async function synchronize(): Promise<{ results: SyncOutcome[]; labels: Record<string, string> }> {
  const commands = await pending();
  if (commands.length === 0) return { results: [], labels: {} };
  const results = await api<SyncOutcome[]>('/sync/commandes', {
    method: 'POST',
    body: {
      deviceId: deviceId(),
      commands: commands.map(({ commandId, type, applicationId, knownVersion, payload }) => ({
        commandId,
        type,
        applicationId,
        knownVersion,
        payload,
      })),
    },
  });
  const db = await idb();
  for (const r of results) {
    if (r.outcome !== 'conflit') await tx(db, 'commandes', 'readwrite', (s) => s.delete(r.commandId));
  }
  return { results, labels: Object.fromEntries(commands.map((c) => [c.commandId, c.label])) };
}

export async function discard(commandId: string): Promise<void> {
  const db = await idb();
  await tx(db, 'commandes', 'readwrite', (s) => s.delete(commandId));
}
